import {
  useState,
  useCallback,
  createContext,
  useContext,
  useEffect,
  useRef,
  useMemo,
  Dispatch,
  SetStateAction,
  ReactNode,
  MutableRefObject,
} from 'react'

import { DashboardId, DashboardPool } from '@/types/domain'

import { authHeader, notifyError, notifySuccess } from '../api'
import { usePreloader } from '../components'
import { languageConstants as LC } from '../constants'

import { refreshToken } from './auth'

const { REACT_APP_API_URL } = import.meta.env

// types

type DashboardContextValue = {
  data: DashboardPool
  setData: Dispatch<SetStateAction<DashboardPool>>
  inflightRef: MutableRefObject<Map<string, Promise<DashboardPool>>>
  fetchedRef: MutableRefObject<boolean>
}

// context (mit Defaults, stabil)
const DashboardCtx = createContext<DashboardContextValue>({
  data: [],
  setData: () => {},
  inflightRef: { current: new Map() },
  fetchedRef: { current: false },
})

// provider
type Props = { children: ReactNode }

export const DashboardDataProvider = ({ children }: Props) => {
  const [data, setData] = useState<DashboardPool>([])
  const inflightRef = useRef<Map<string, Promise<DashboardPool>>>(new Map())
  const fetchedRef = useRef<boolean>(false)

  const { getPool } = useDashboardApi({
    data,
    setData,
    inflightRef,
    fetchedRef,
  })

  useEffect(() => {
    let active = true
    ;(async () => {
      try {
        await getPool()
      } catch (e: any) {
        if (active) notifyError(`DashboardApi: ${e?.message}`)
      }
    })()
    return () => {
      active = false
    }
  }, [getPool])

  const value = useMemo(
    () => ({ data, setData, inflightRef, fetchedRef }),
    [data]
  )

  return <DashboardCtx.Provider value={value}>{children}</DashboardCtx.Provider>
}

// pool hook
export const useDashboardPool = (): DashboardPool =>
  useContext(DashboardCtx).data

// api hook
export const useDashboardApi = (
  overrides?: Pick<
    DashboardContextValue,
    'data' | 'setData' | 'inflightRef' | 'fetchedRef'
  >
) => {
  const { start, stop } = usePreloader()
  const ctx = useContext(DashboardCtx)

  const source = { ...ctx, ...(overrides ?? {}) }
  const { data, setData, inflightRef, fetchedRef } = source

  const withRequest = useCallback(
    async <T,>(fn: () => Promise<T>): Promise<T | null> => {
      try {
        await refreshToken()
        return await fn()
      } catch (error: any) {
        const status = error?.status ?? error?.response?.status
        if (status === 401 || status === 403) {
          try {
            await refreshToken()
            return await fn()
          } catch (e2: any) {
            notifyError(`DashboardApi: ${e2?.message}`)
            return null
          }
        }
        notifyError(`DashboardApi: ${error?.message}`)
        return null
      }
    },
    []
  )

  const withPreloader = useCallback(
    async <T,>(fn: () => Promise<T>): Promise<T> => {
      start()
      try {
        return await fn()
      } finally {
        stop()
      }
    },
    [start, stop]
  )

  const getPool = useCallback(
    async (silent = false): Promise<DashboardPool> => {
      if (fetchedRef.current && !silent) return data

      const key = 'dashboards:list'
      const inflight = inflightRef.current.get(key)
      if (inflight && !silent) return inflight

      const exec = () =>
        withRequest(async () => {
          const res = await fetch(`${REACT_APP_API_URL}/dashboard`, {
            method: 'GET',
            headers: { ...authHeader() },
          })

          if (res.status === 204 || res.status === 404) {
            setData([])
            fetchedRef.current = true
            return []
          }

          if (!res.ok) {
            const text = await res.text()
            const err = new Error(text || `HTTP ${res.status}`)
            ;(err as any).status = res.status
            throw err
          }

          const payload = await res.json()
          const result = payload?.items?.data ?? payload?.data ?? []
          setData(result)
          fetchedRef.current = true
          return result
        })

      const p = (silent ? exec() : withPreloader(exec)).finally(() =>
        inflightRef.current.delete(key)
      )

      inflightRef.current.set(key, p)
      return p
    },
    [data, fetchedRef, inflightRef, setData, withPreloader, withRequest]
  )

  const save = useCallback(
    (form: any) =>
      withPreloader(() =>
        withRequest(async () => {
          const { name, user_id } = form || {}
          if (!name) throw new Error(LC.DASHBOARD_NAME_REQUIRED)

          const res = await fetch(`${REACT_APP_API_URL}/dashboard`, {
            method: 'POST',
            headers: {
              ...authHeader(),
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ data: { attributes: { user_id, name } } }),
          })

          if (!res.ok) throw new Error(await res.text())
          notifySuccess(LC.DASHBOARD_SAVED)
          fetchedRef.current = false
          getPool(true).catch(() => null)
          return true
        })
      ),
    [getPool, withPreloader, withRequest, fetchedRef]
  )

  const update = useCallback(
    (id: DashboardId, form: any) =>
      withPreloader(() =>
        withRequest(async () => {
          const { name, user_id } = form || {}
          if (!name) throw new Error(LC.DASHBOARD_NAME_REQUIRED)

          const res = await fetch(`${REACT_APP_API_URL}/dashboard/${id}`, {
            method: 'PATCH',
            headers: {
              ...authHeader(),
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ data: { attributes: { user_id, name } } }),
          })

          if (!res.ok) throw new Error(await res.text())
          notifySuccess(LC.DASHBOARD_UPDATED)
          fetchedRef.current = false
          getPool(true).catch(() => null)
          return true
        })
      ),
    [getPool, withPreloader, withRequest, fetchedRef]
  )

  const remove = useCallback(
    (id: DashboardId) =>
      withPreloader(() =>
        withRequest(async () => {
          if (!id) throw new Error(LC.DASHBOARD_ID_REQUIRED)

          const res = await fetch(`${REACT_APP_API_URL}/dashboard/${id}`, {
            method: 'DELETE',
            headers: { ...authHeader() },
          })

          if (!res.ok) throw new Error(await res.text())
          notifySuccess(LC.DASHBOARD_DELETED)
          fetchedRef.current = false
          getPool(true).catch(() => null)
          return true
        })
      ),
    [getPool, withPreloader, withRequest, fetchedRef]
  )

  return { getPool, save, update, remove }
}
